import type {HistoryRecord, HistoryStatus} from '../../history/data/mockHistory'
import {industryOptions} from '../../promotions/data/mockOptions'
import {parseBrDatetime} from '../../shared/utils/dateUtils'

export const STATUS_PERMISSION = 'briefing-promocoes-status-view'

export const STATUS_COLUMNS = [
  {id: 'not_sent', title: 'Não enviadas', description: 'Aguardando envio ao Pipefy', tone: 'neutral'},
  {id: 'queued', title: 'Na fila', description: 'Envio solicitado', tone: 'blue'},
  {id: 'processing', title: 'Em processamento', description: 'Envio em andamento', tone: 'amber'},
  {id: 'sent', title: 'Enviadas', description: 'Enviadas ao Pipefy', tone: 'green'},
  {id: 'failed', title: 'Falha no envio', description: 'Precisam de atenção', tone: 'red'},
  {id: 'cancelled', title: 'Canceladas', description: 'Envio cancelado', tone: 'neutral'},
  {id: 'unknown', title: 'Status não identificado', description: 'Etapa não reconhecida', tone: 'neutral'},
] as const

export type StatusStage = typeof STATUS_COLUMNS[number]['id']
export type StatusFilters = {
  search: string
  stage: string
  validity: HistoryStatus | ''
  template: string
  industry: string
  author: string
  from: string
  to: string
}
export type StatusSort = 'recent' | 'start' | 'name'

export const EMPTY_STATUS_FILTERS: StatusFilters = {
  search: '', stage: '', validity: '', template: '', industry: '', author: '', from: '', to: '',
}

export const VALIDITY_LABELS: Record<HistoryStatus, string> = {
  ativo: 'Ativa', agendado: 'Agendada', encerrado: 'Encerrada', rascunho: 'Sem vigência definida',
}

export function getStatusStage(record: HistoryRecord): StatusStage {
  const status = record.pipefyStatus || 'not_sent'
  if (status === 'sent_manual') return 'sent'
  return STATUS_COLUMNS.some((column) => column.id === status) ? status as StatusStage : 'unknown'
}

export function valueText(value: unknown): string {
  return Array.isArray(value) ? value.join(', ') : String(value ?? '')
}

export function industryLabel(value: unknown): string {
  const id = valueText(value)
  return industryOptions.find((option) => option.value === id)?.label.replace(/^\d+\s*—\s*/, '') || id || 'Não informada'
}

export function promotionName(record: HistoryRecord): string {
  return valueText(record.values.nomePromo) || valueText(record.values.nomeExibicao) || `Promoção #${record.id}`
}

function normalized(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim()
}

export function parseStatusDate(value: string): Date | null {
  if (!value) return null
  const date = parseBrDatetime(value) || new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function dateKey(value: unknown): string {
  const date = parseStatusDate(valueText(value))
  if (!date) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function formatStatusDate(value: string): string {
  return parseStatusDate(value)?.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) || 'Não informada'
}

export function filterStatusRecords(records: HistoryRecord[], filters: StatusFilters, sort: StatusSort): HistoryRecord[] {
  const search = normalized(filters.search)
  const invalidRange = filters.from && filters.to && filters.from > filters.to
  return records.filter((record) => {
    if (invalidRange) return false
    if (filters.stage && getStatusStage(record) !== filters.stage) return false
    if (filters.validity && record.status !== filters.validity) return false
    if (filters.template && record.templateId !== filters.template) return false
    if (filters.industry && valueText(record.values.industria) !== filters.industry) return false
    if (filters.author && record.author !== filters.author) return false
    const start = dateKey(record.values.inicioVigencia)
    const end = dateKey(record.values.fimVigencia)
    // O período selecionado inclui promoções cuja vigência se sobrepõe a ele.
    if (filters.from && (!end || end < filters.from)) return false
    if (filters.to && (!start || start > filters.to)) return false
    if (!search) return true
    return normalized([
      record.id, `#${record.id}`, promotionName(record), valueText(record.values.nomeExibicao),
      valueText(record.values.idIFC), record.author, record.templateName,
      industryLabel(record.values.industria),
    ].join(' ')).includes(search)
  }).sort((left, right) => {
    if (sort === 'name') return promotionName(left).localeCompare(promotionName(right), 'pt-BR')
    if (sort === 'start') {
      const start = (parseStatusDate(valueText(left.values.inicioVigencia))?.getTime() ?? Infinity) -
        (parseStatusDate(valueText(right.values.inicioVigencia))?.getTime() ?? Infinity)
      if (start && !Number.isNaN(start)) return start
    }
    return right.id.localeCompare(left.id, undefined, {numeric: true})
  })
}
